/**
 * assets/v2-cart-drawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Global Cart Drawer for Shopify themes — Optimized v2
 *
 * PUBLIC API (CustomEvents dispatched on `document`):
 *   cart:open    — opens the drawer and refreshes cart data
 *   cart:close   — closes the drawer
 *   cart:refresh — re-fetches cart data without toggling visibility
 *
 * DATA ATTRIBUTES (sprinkle anywhere in your theme HTML):
 *   data-v2-cart-drawer-open   → opens the drawer on click
 *   data-v2-cart-drawer-close  → closes the drawer on click
 *
 * CART COUNT BADGES (auto-updated):
 *   any element with [data-v2-cart-count] will have its textContent updated
 *   on every cart change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Element refs ───────────────────────────────────────────────────────────
  const DRAWER                  = document.getElementById('v2-cart-drawer');
  const OVERLAY                 = document.getElementById('v2-cart-drawer-overlay');
  const ITEMS_EL                = document.getElementById('v2-cart-drawer-items');
  const UPSELL_EL               = document.getElementById('v2-cart-drawer-upsell');
  const UPSELL_ADD_OR_REMOVE    = document.getElementById('v2-upsell-item-add-or-remove-btn');
  const EMPTY_EL                = document.getElementById('v2-cart-drawer-empty');
  const FOOTER_EL               = document.getElementById('v2-cart-drawer-footer');
  const COUNT_EL                = document.getElementById('v2-cart-drawer-count');
  const SUB_EL                  = document.getElementById('v2-cart-drawer-subtotal');
  const BTN_SUB_EL              = document.getElementById('v2-cart-drawer-btn-subtotal-text');
  const LOAD_EL                 = document.getElementById('v2-cart-drawer-loading');
  const TEMPLATE                = document.getElementById('v2-cart-drawer-item-template');
  const FREE_SHIPPING_EL        = document.getElementById('v2-cart-free-shipping');
  const FREE_SHIPPING_TEXT      = document.getElementById('v2-cart-free-shipping-text');
  const FREE_SHIPPING_PROGRESS  = document.getElementById('v2-cart-free-shipping-progress');
  const FREE_SHIPPING_LEFT      = document.getElementById('v2-cart-free-shipping-left-amount');

  if (!DRAWER) return; // snippet not rendered — bail out safely

  // ── Config ─────────────────────────────────────────────────────────────────
  const FREE_SHIPPING_THRESHOLD   = 20000;           // cents
  const TARGET_SCENT_PODS_VARIANT = 45601206173798;
   const SELLING_PLAN_ID           = 3567059046; // Loop
  // const SELLING_PLAN_ID           = 3425828966; //seal
  const SCENT_PODS_URL            = '/products/spin360-scent-pods-starter-pack.js';
  const QTY_DEBOUNCE_MS           = 400;

  // Line-item property that marks the auto/upsell pods line as OUR deal, so it
  // stays distinct from a customer-added pods line of the same variant.
  const UPSELL_PROPS = { _upsell: 'true' };

  // ── Feature flags ────────────────────────────────────────────────
  const HIDE_UPSELL      = false;  // true → hides upsell UI *and* disables auto-add
  const DISABLE_AUTO_ADD = true;  // true → disables auto-add only (UI stays visible)

  // derived — auto-add runs only when it's not disabled AND the upsell isn't hidden
  const AUTO_ADD_ENABLED = !DISABLE_AUTO_ADD && !HIDE_UPSELL;

  // ── State ──────────────────────────────────────────────────────────────────
  let isFetching       = false;
  let fetchQueued      = false;   //  queue instead of drop
  let cartCash = null // cart product cash
  let cachedUpsell     = null;    // upsell product cache
  let qtyDebounceTimer = null;    //  debounce handle

  // ── Money formatter ────────────────────────────────────────────────────────
  const moneyFormatter = new Intl.NumberFormat(
    document.documentElement.lang || 'en',
    { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }
  );
  function formatMoney(cents) {
    return moneyFormatter.format(cents / 100);
  }

  // ── Session helpers ────────────────────────────────────────────────────────
  function hasAutoAddedScentPods() {
    return sessionStorage.getItem('scentPodsAutoAdded') === 'true';
  }
  function setAutoAddedScentPods() {
    sessionStorage.setItem('scentPodsAutoAdded', 'true');
  }

  // ── Open / Close ───────────────────────────────────────────────────────────
  function openDrawer() {
    DRAWER.classList.add('is-open');
    OVERLAY.classList.add('is-visible');
    document.body.classList.add('v2-cart-drawer-open');
    DRAWER.setAttribute('aria-hidden', 'false');
    OVERLAY.setAttribute('aria-hidden', 'false');

    const firstFocusable = DRAWER.querySelector('button, [href], input');
    if (firstFocusable) firstFocusable.focus();

    requestAnimationFrame(() => {
      showSkeleton();
      fetchCart();
    })

  }

  function closeDrawer() {
    DRAWER.classList.remove('is-open');
    OVERLAY.classList.remove('is-visible');
    document.body.classList.remove('v2-cart-drawer-open');
    DRAWER.setAttribute('aria-hidden', 'true');
    OVERLAY.setAttribute('aria-hidden', 'true');
  }

  // ── Skeleton loader ───────────────────────────────────────────────
  function showSkeleton() {
    // Only inject skeleton when the list is empty (first open)
    if (ITEMS_EL.children.length > 0) return;

    // Inject the pulse keyframe once
    if (!document.getElementById('cd-skeleton-style')) {
      const s = document.createElement('style');
      s.id = 'cd-skeleton-style';
      s.textContent = '@keyframes cd-pulse{0%,100%{opacity:.35}50%{opacity:.6}}';
      document.head.appendChild(s);
    }

    ITEMS_EL.innerHTML = Array(2).fill(`
      <li class="v2-cart-item" style="opacity:.35;pointer-events:none;animation:cd-pulse 1.4s ease-in-out infinite;">
        <div style="width:80px;height:100px;background:var(--cd-border);border-radius:5px;flex-shrink:0;"></div>
        <div style="flex:1;display:flex;flex-direction:column;gap:10px;padding-top:6px;">
          <div style="height:13px;width:65%;background:var(--cd-border);border-radius:4px;"></div>
          <div style="height:11px;width:40%;background:var(--cd-border);border-radius:4px;"></div>
          <div style="height:28px;width:90px;background:var(--cd-border);border-radius:8px;margin-top:auto;"></div>
        </div>
      </li>
    `).join('');
    ITEMS_EL.hidden = false;
    FOOTER_EL.hidden = false;
  }

  // ── Prefetch on cart-icon hover ──────────────────────────────────
  document.addEventListener('pointerenter', (e) => {
    if (!(e.target instanceof Element)) return;
    if (!e.target.closest('[data-v2-cart-drawer-open]')) return;
    if (isFetching || window._prefetchedCart) return;
    fetch('/cart.js')
        .then(r => r.json())
        .then(cart => { window._prefetchedCart = cart; })
        .catch(() => {});
  }, true);

  // ── Cached upsell product fetch ──────────────────────────────────
  async function getUpsellProduct() {
    if (cachedUpsell) return cachedUpsell;
    try {
      const res = await fetch(SCENT_PODS_URL);
      cachedUpsell = await res.json();
      return cachedUpsell;
    } catch (err) {
      console.warn('[CartDrawer] Could not load upsell product:', err);
      return null;
    }
  }

  // ── fetchCart — parallel, prefetch-aware, queued ────────────
  async function fetchCart(skipAutoAdd = false) {
    //  Queue instead of silently dropping concurrent calls
    if (isFetching) {
      fetchQueued = true;
      return;
    }

    isFetching = true;
    showLoading(true);

    try {
      //  Run cart + upsell product fetches in parallel
      //  Consume prefetched cart if available
      const prefetchedCart = consumePrefetch();
      const cartPromise = prefetchedCart
        ? Promise.resolve(prefetchedCart)
        : fetch('/cart.js').then(r => r.json());

      const [cart, upsellProduct] = await Promise.all([
        cartPromise,
        getUpsellProduct(), //  cached after first resolution
      ]);

      // Auto-add scent pods when needed (must stay sequential — affects cart state)
      // Guarded by: feature flag, caller opt-out, non-empty cart, our deal not
      // already present, the customer's OWN scent pods not present (stand down),
      // and the once-per-session flag.
      let finalCart = cart;
      if (
        AUTO_ADD_ENABLED &&
        !skipAutoAdd &&
        cart.item_count > 0 &&
        !checkScentPodsInCart(cart) &&
        !findCustomerScentPods(cart) &&
        !hasAutoAddedScentPods()
      ) {
        await addItemToCart({
          id:           TARGET_SCENT_PODS_VARIANT,
          quantity:     1,
          selling_plan: SELLING_PLAN_ID,
          properties:   { ...UPSELL_PROPS },
        });
        finalCart = await fetch('/cart.js').then(r => r.json());
        setAutoAddedScentPods();
      }

      cartCash = finalCart;
      renderCart(finalCart, upsellProduct);

    } catch (err) {
      console.error('[CartDrawer] Failed to fetch cart:', err);
    } finally {
      isFetching = false;
      showLoading(false);

      //  Drain queued fetch
      if (fetchQueued) {
        fetchQueued = false;
        fetchCart();
      }
    }
  }

  // ── prefetch helper (consume correctly) ───────────────────────────────────
  function consumePrefetch() {
    const cached = window._prefetchedCart || null;
    window._prefetchedCart = null;
    return cached;
  }

  // ── Check helpers ──────────────────────────────────────────────────────────
  function checkScentPodsInCart(cart) {
    return cart.items.find(item => {
      return item.variant_id === TARGET_SCENT_PODS_VARIANT;
    });
  }

  // Identity helper — THE spine of the upsell logic.
  // The upsell line = pods variant AND our _upsell tag. This is what lets the
  // customer's own pods line and our deal coexist without colliding.
  function findUpsellLine(cart) {
    return cart?.items?.find(item =>
      item.variant_id === TARGET_SCENT_PODS_VARIANT &&
      item.properties?._upsell === 'true'
    );
  }

  // Per-item predicate version (for sort / reconcile, where there's no cart to search)
  function isUpsellLine(item) {
    return item.variant_id === TARGET_SCENT_PODS_VARIANT &&
           item.properties?._upsell === 'true';
  }

  // The customer's OWN scent pods = any product typed "Scent Pods" that is NOT
  // our upsell variant. The upsell starter pack is upsell-only (a customer can't
  // add it), so product_type — not variant — is what identifies pods the
  // customer chose for themselves.
  function findCustomerScentPods(cart) {
    return cart?.items?.find(item =>
      item.product_type === 'Scent Pods' &&
      item.variant_id !== TARGET_SCENT_PODS_VARIANT
    );
  }

  // "Stand down" = the customer has their OWN scent pods and we have no deal line
  // → neither auto-add nor show the upsell UI. (If our deal is also present we
  // keep the widget so it isn't left orphaned.)
  function isStandDown(cart) {
    return !!findCustomerScentPods(cart) && !checkScentPodsInCart(cart);
  }

  function isScentPodsExist(cart) {
    return cart.items.find(item => {
      return item.product_type === 'Scent Pods';
    });
  }

  // ── Render full cart (called after every fetchCart) ────────────────────────
  function renderCart(cart, upsellProduct) {
    const count = cart.item_count;

    // Count badge (header + page-wide badges)
    if (COUNT_EL) {
      COUNT_EL.textContent = count > 0
        ? `(${count} ${count === 1 ? 'item' : 'items'})`
        : '';
    }
    document.querySelectorAll('[data-v2-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });

    renderFreeShipping(cart);
    if (SUB_EL) SUB_EL.textContent = formatMoney(cart.items_subtotal_price);
    if (BTN_SUB_EL) BTN_SUB_EL.textContent = ` - ${formatMoney(cart.items_subtotal_price)}`;

    if (count === 0) {
      FREE_SHIPPING_EL.hidden = true;
      ITEMS_EL.hidden         = true;
      UPSELL_EL.hidden        = true;
      FOOTER_EL.hidden        = true;
      EMPTY_EL.hidden         = false;
      return;
    }

    EMPTY_EL.hidden = true;

    // Upsell visibility:
    //   stand down (hide) when the customer added their OWN pods and we have no deal;
    //   otherwise reflect our tagged deal, or offer pods when there are none at all.
    const upsellDeal = findUpsellLine(cart);
    const standDown  = isStandDown(cart);
    if (!standDown)
      renderUpsellProduct(upsellProduct, upsellDeal);
    

    // Sort: the tagged upsell line always last (customer-added pods keep their place)
    const sortedItems = [...cart.items].sort((a, b) => {
      if (isUpsellLine(a)) return 1;
      if (isUpsellLine(b)) return -1;
      return 0;
    });

    // Reconcile DOM — add/update/remove, never full wipe
    reconcileItems(sortedItems);

    FREE_SHIPPING_EL.hidden = false;
    ITEMS_EL.hidden         = false;
    UPSELL_EL.hidden        = HIDE_UPSELL || standDown;
    FOOTER_EL.hidden        = false;
    // if(!isScentPodsExist) {
    //   UPSELL_EL.hidden        = false;
    // }
  }

  // ── Reconcile items list (diff render) ────────────────────────────
  function reconcileItems(items) {
    const existingKeys = new Set(
      Array.from(ITEMS_EL.querySelectorAll('.v2-cart-item')).map(el => el.dataset.key)
    );
    const newKeys = new Set(items.map(i => String(i.key)));

    // Remove skeleton
    ITEMS_EL.querySelectorAll('.v2-cart-item:not([data-key])').forEach(el => el.remove());

    // Remove rows no longer in cart
    existingKeys.forEach(key => {
      if (!newKeys.has(key)) {
        ITEMS_EL.querySelector(`.v2-cart-item[data-key="${key}"]`)?.remove();
      }
    });

    // Add new rows or patch existing ones
    items.forEach(item => {
      const existing = ITEMS_EL.querySelector(`.v2-cart-item[data-key="${item.key}"]`);

      if (existing) {
        // Patch price
        const priceEl = existing.querySelector('.v2-cart-item__price');
        if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);

        // Patch qty (only if user isn't actively typing in this field)
        const qtyInput = existing.querySelector('.v2-cart-item__qty-input');
        if (qtyInput && document.activeElement !== qtyInput) {
          qtyInput.value = item.quantity;
        }

        // Patch original / compare-at price
        const origEl = existing.querySelector('.v2-cart-item__original-price');
        if (origEl) {
          if (item.original_line_price !== item.final_line_price) {
            origEl.textContent = formatMoney(item.original_line_price);
            origEl.hidden = false;
          } else {
            origEl.hidden = true;
          }
        }
      } else {
        // New item — build and append.
        // Skip ONLY the tagged upsell line (it's represented by the widget);
        // a customer-added pods line renders as a normal row.
        if (!isUpsellLine(item))
          buildLineItem(item);
      }
    });
  }

  // ── Render upsell product ──────────────────────────────────────────────────
  function renderUpsellProduct(product, podInCart) {
    const upsellEl = document.querySelector('.v2-cart-drawer__upsell');
    const planToggleButton = document.querySelectorAll('input[name="purchaseType"]');
    if (!upsellEl) return;
    if (HIDE_UPSELL) {                 // flag: hide UI entirely
      if (UPSELL_EL) UPSELL_EL.hidden = true;
      return;
    }
    if (!product) return;              // upsell fetch failed — nothing to render

    const spItem = findUpsellLine(cartCash);

    // clear button active state
    planToggleButton.forEach(plan => {
      if (!(plan instanceof Element)) return;
      plan.classList.remove('active');
    });

    if(podInCart && UPSELL_ADD_OR_REMOVE) 
      UPSELL_ADD_OR_REMOVE.checked = true;
    else
      UPSELL_ADD_OR_REMOVE.checked = false;

    const isUpsellChecker = UPSELL_ADD_OR_REMOVE ? UPSELL_ADD_OR_REMOVE.checked : false;

    if (isUpsellChecker && podInCart && spItem.selling_plan_allocation) {
      planToggleButton.forEach(plan => {
        const label = plan.closest('.v2-upsell-item__plan');

        if (plan.value === 'subscribe') {
          label?.classList.add('active');
          plan.checked = true;
        } else {
          label?.classList.remove('active');
          plan.checked = false;
        }
      });
    } else if(isUpsellChecker && podInCart && !spItem.selling_plan_allocation) {
      planToggleButton.forEach(plan => {
        const label = plan.closest('.v2-upsell-item__plan');

        if (plan.value === 'onetime') {
          label?.classList.add('active');
          plan.checked = true;
        } else {
          label?.classList.remove('active');
          plan.checked = false;
        }
      });
    } else {
      planToggleButton.forEach(plan => {
        const label = plan.closest('.v2-upsell-item__plan');

        if (plan.value === 'subscribe') {
          label?.classList.add('active');
          plan.checked = true;
        } else {
          label?.classList.remove('active');
          plan.checked = false;
        }
      });
    }

    const img = upsellEl.querySelector('.v2-upsell-item__image');
    if (img && product.featured_image) img.src = product.featured_image;
  }

  // ── Build single line-item node from <template> ────────────────────────────
  function buildLineItem(item, showQuantity = true, isSubscription = false) {
    const node = TEMPLATE.content.cloneNode(true);
    const li   = node.querySelector('.v2-cart-item');

    li.dataset.variantId = String(item.variant_id);
    li.dataset.key       = String(item.key);

    // Image
    const img     = li.querySelector('.v2-cart-item__image');
    const imgLink = li.querySelector('.v2-cart-item__image-link');
    if (item.image) {
      img.src = item.image.replace('_1x1.', '_120x120.');
      img.alt = item.product_title;
    }
    imgLink.href = item.url;

    // Title
    const titleEl = li.querySelector('.v2-cart-item__title');
    titleEl.textContent = item.product_title;
    titleEl.href        = item.url;

    // Variant
    if (item.variant_title) {
      const variantEl = li.querySelector('.v2-cart-item__variant');
      variantEl.textContent = item.variant_title;
      variantEl.hidden = false;
    }

    // Prices
    li.querySelector('.v2-cart-item__price').textContent = formatMoney(item.final_line_price);
    if (item.original_line_price !== item.final_line_price) {
      const origEl = li.querySelector('.v2-cart-item__original-price');
      origEl.textContent = formatMoney(item.original_line_price);
      origEl.hidden = false;
    }

    // Quantity controls
    if (showQuantity) {
      li.querySelector('.v2-cart-item__quantity-control').hidden = false;

      const qtyInput = li.querySelector('.v2-cart-item__qty-input');
      qtyInput.value       = item.quantity;
      qtyInput.dataset.key = String(item.key);

      li.querySelector('[data-action="decrease"]').addEventListener('click', () => {
        const newQty = Math.max(0, parseInt(qtyInput.value) - 1);
        qtyInput.value = newQty; //  optimistic
        updateItem(item.key, newQty);
      });

      li.querySelector('[data-action="increase"]').addEventListener('click', () => {
        const newQty = parseInt(qtyInput.value) + 1;
        qtyInput.value = newQty; //  optimistic
        updateItem(item.key, newQty);
      });

      //  Debounced free-text input
      qtyInput.addEventListener('input', () => {
        clearTimeout(qtyDebounceTimer);
        qtyDebounceTimer = setTimeout(() => {
          const newQty = Math.max(0, parseInt(qtyInput.value) || 0);
          updateItem(item.key, newQty);
        }, QTY_DEBOUNCE_MS);
      });
    } else {
      li.querySelector('.v2-cart-item__quantity-control').hidden = true;
    }

    // Remove button — optimistic opacity before fetch
    li.querySelector('.v2-cart-item__remove').addEventListener('click', () => {
      li.style.opacity        = '0.4';
      li.style.pointerEvents  = 'none';
      updateItem(item.key, 0);
    });

    ITEMS_EL.appendChild(node);
  }

  // ── Optimistic update + DOM patch ───────────────────────────
  async function updateItem(key, quantity) {
    // Optimistic: update input immediately so the UI feels instant
    const qtyInput = ITEMS_EL.querySelector(`.v2-cart-item__qty-input[data-key="${key}"]`);
    if (qtyInput) qtyInput.value = quantity;

    try {
      const res = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity }),
      });

      if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);

      const cart = await res.json();

      cartCash = cart;

      // Patch only totals + badge + remove empty rows
      patchCartTotals(cart);

    } catch (err) {
      console.error('[CartDrawer] Failed to update item:', err);
      // Full refresh to restore correct state after any failure
      fetchCart(true);
    } 
  }

  // ── Patch totals/badges without rebuilding item list ─────────────
  function patchCartTotals(cart) {
    const count = cart.item_count;

    if (COUNT_EL) {
      COUNT_EL.textContent = count > 0
        ? `( ${count} ${count === 1 ? 'item' : 'items'} )`
        : '';
    }
    document.querySelectorAll('[data-v2-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });

    if (SUB_EL) SUB_EL.textContent = formatMoney(cart.items_subtotal_price);
    if (BTN_SUB_EL) BTN_SUB_EL.textContent = ` - ${formatMoney(cart.items_subtotal_price)}`;
    renderFreeShipping(cart);

    const sortedItems = [...cart.items].sort((a, b) => {
      if (isUpsellLine(a)) return 1;
      if (isUpsellLine(b)) return -1;
      return 0;
    });
    reconcileItems(sortedItems);

    // Stand down (hide) if the customer's own pods are present with no deal;
    // otherwise (re)offer only when there are no pods of any kind left.
    if (isStandDown(cart)) {
      UPSELL_EL.hidden = true;
    } else if (count > 0 && !checkScentPodsInCart(cart)) {
      getUpsellProduct().then(product => {
        if (product) {
          renderUpsellProduct(product);
          UPSELL_EL.hidden = HIDE_UPSELL;
        }
      });
    }

    if (count === 0) {
      FREE_SHIPPING_EL.hidden = true;
      ITEMS_EL.hidden         = true;
      UPSELL_EL.hidden        = true
      FOOTER_EL.hidden        = true;
      EMPTY_EL.hidden         = false;
    }
  }

  // ── Add item to cart (shared helper) ──────────────────────────────────────
  async function addItemToCart(payload) {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to add item: ${res.status}`);
    return res.json();
  }

  // ── Replace cart item (swap selling plan) ──────────────────────────────────
  async function replaceCartItem(item, newSellingPlanId = null) {
    // ✅ Optimistic: update UI immediately before any fetch
    const subEl     = ITEMS_EL.querySelector(`.v2-cart-item[data-key="${item.key}"] .v2-cart-item__subscription`);
    const oneTimeEl = ITEMS_EL.querySelector(`.v2-cart-item[data-key="${item.key}"] .v2-cart-item__onetime`);
    if (newSellingPlanId) {
      subEl?.removeAttribute('hidden');
      oneTimeEl?.setAttribute('hidden', '');
    } else {
      oneTimeEl?.removeAttribute('hidden');
      subEl?.setAttribute('hidden', '');
    }

    showLoading(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
      // Re-add MUST carry the tag, or the swapped line stops being recognisable
      // as the upsell line.
      const payload = { id: item.variant_id, quantity: 1, properties: { ...UPSELL_PROPS } };
      if (newSellingPlanId) payload.selling_plan = newSellingPlanId;
      await addItemToCart(payload);
      await fetchCart(true); // sync true state after
    } catch (err) {
      console.error('[CartDrawer] replaceCartItem error:', err);
      fetchCart(true); // rollback on failure
    } finally {
      showLoading(false);
    }
  }

  // ── Add scent pods upsell ──────────────────────────────────────────────────
  async function addScentPods(payload) {
    showLoading(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Failed to add item: ${res.status}`);

      // ✅ /cart/add.js doesn't return full cart, so fetch it — but reuse upsell cache
      const [cart, upsellProduct] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        getUpsellProduct(), // already cached, zero cost
      ]);
      cartCash = cart;
      renderCart(cart, upsellProduct); // single render, no double fetch
    } catch (err) {
      console.error('[CartDrawer] Upsell add error:', err);
    } finally {
      showLoading(false);
    }
  }

  // ── Free shipping progress bar ────────────────────────────────────────────
  function renderFreeShipping(cart) {
    if (!FREE_SHIPPING_EL) return;

    const subtotal  = cart.items_subtotal_price;
    const remaining = FREE_SHIPPING_THRESHOLD - subtotal;
    const progress  = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

    FREE_SHIPPING_PROGRESS.style.width = `${progress}%`;

    if (remaining > 0) {
      FREE_SHIPPING_TEXT.textContent = `You're ${formatMoney(remaining)} away from FREE shipping`;
      FREE_SHIPPING_LEFT.textContent = `${formatMoney(remaining)} left`;
      FREE_SHIPPING_EL.style.background = '#FBF1E3';
    } else {
      FREE_SHIPPING_TEXT.textContent = 'You unlocked FREE shipping';
      FREE_SHIPPING_LEFT.textContent = 'Free';
      FREE_SHIPPING_EL.style.background = '#E2E7DB';
    }
  }

  // ── Loading overlay ────────────────────────────────────────────────────────
  function showLoading(show) {
    LOAD_EL.classList.toggle('is-active', show);
    LOAD_EL.setAttribute('aria-hidden', String(!show));
  }

  // ── Upsell plan selector ───────────────────────────────────────────────────
  document.querySelectorAll('.v2-upsell-item__purchase-plan-toggler')
  .forEach(toggler => {

    toggler.querySelectorAll('input[name="purchaseType"]')
      .forEach(input => {

        let planToggleTimer = null;

        // In your change event listener:
        input.addEventListener('change', () => {
          // Update active class instantly
          toggler.querySelectorAll('.v2-upsell-item__plan').forEach(plan => {
            if (!(plan instanceof Element)) return;
            plan.classList.remove('active');
          });
          input.closest('.v2-upsell-item__plan')?.classList.add('active');

          // Debounce the actual API call
          clearTimeout(planToggleTimer);
          planToggleTimer = setTimeout(() => {
            const item = findUpsellLine(cartCash);
            if (item && UPSELL_ADD_OR_REMOVE?.checked) {
              replaceCartItem(item, input.value === 'subscribe' ? SELLING_PLAN_ID : null);
            }
          }, 400);
        });

      });

  });

  UPSELL_ADD_OR_REMOVE.addEventListener('click', (e) => {
    if (HIDE_UPSELL) return;   // flag: behavior matches hidden UI
    const plans = document.querySelectorAll('.v2-upsell-item__plan');

    const checkedInput = [...plans]
      .map(p => p.querySelector('input[name="purchaseType"]'))
      .find(input => input?.checked);

    if (checkedInput) {
      const item = findUpsellLine(cartCash);
      if(item){
        updateItem(item.key, 0);
      } else {
        const payload = {
          id: TARGET_SCENT_PODS_VARIANT,
          quantity: 1,
          properties: { ...UPSELL_PROPS }
        };

        if (checkedInput.value === 'subscribe') {
          payload.selling_plan = SELLING_PLAN_ID;
        }

        addScentPods(payload);
      }
    } else {
      addScentPods({
        id: TARGET_SCENT_PODS_VARIANT,
        quantity: 1,
        selling_plan: SELLING_PLAN_ID,
        properties: { ...UPSELL_PROPS }
      });
    }
  });


  // ── Global CustomEvent API ─────────────────────────────────────────────────
  document.addEventListener('cart:open',    openDrawer);
  document.addEventListener('cart:close',   closeDrawer);
  document.addEventListener('cart:refresh', () => fetchCart());

  // ── Data-attribute click delegation ───────────────────────────────────────
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-v2-cart-drawer-open]'))  openDrawer();
    if (e.target.closest('[data-v2-cart-drawer-close]')) closeDrawer();
  });

  // ── Keyboard: Escape closes drawer ────────────────────────────────────────
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && DRAWER.classList.contains('is-open')) closeDrawer();
  });

  // ── Focus trap ─────────────────────────────────────────────────────────────
  DRAWER.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !DRAWER.classList.contains('is-open')) return;
    const focusable = Array.from(
      DRAWER.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // ── Global API (inline onclick= / third-party scripts) ────────────────────
  window.CartDrawer = { open: openDrawer, close: closeDrawer, refresh: fetchCart };

  document.addEventListener('DOMContentLoaded', function () {
  try {
    fetch('/browsing_context_suggestions.json')
    .then(response => response.json())
    .then(data => {

      //console.log(data);

      const countryHandle =
        data?.detected_values?.country?.handle;

      if (countryHandle === 'US') {
        FREE_SHIPPING_EL.style.display = 'block';
      }else{
        FREE_SHIPPING_EL.style.display = 'none';
      }

    })
    .catch(error => {
      console.error('Location detection failed:', error);
    });
  } catch (error) {
    console.error(error);
  }

});

})();